// @catalog F.006
// @catalog F.030
// Cells helpers compartidos entre las tablas estilo handoff
// (TopProductsTable, TopMarginProductsTable, StoreMarginAnalysisTable, etc.).
//
// Diseño según prototipo:
// - Header uppercase 11.5px letter-spacing 0.04em color var(--ink-3)
// - Body 13px tabular-nums color var(--ink-1)
// - SKU mono font 11px bg var(--bg-2) padding 2px 5px
// - DeltaPill bg pos-soft/neg-soft, padding 2px 6px, font 11.5px weight 600
import type { ReactNode } from "react";
import { Box } from "@mui/material";

// ─────────────────────────────────────────────────────────────────────────────
// TableShell — wrapper común (Card + tabla scroll-x)
// ─────────────────────────────────────────────────────────────────────────────
interface TableShellProps {
  children: ReactNode;
  // px, o cualquier expresión CSS de alto ("max(360px, calc(100vh - 430px))")
  maxHeight?: number | string;
}

export function TableShell(props: TableShellProps): JSX.Element {
  const { children, maxHeight } = props;
  return (
    <Box
      sx={{
        overflowX: "auto",
        ...(maxHeight && {
          maxHeight:
            typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
          overflowY: "auto",
        }),
      }}
    >
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "13px",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HeaderCell — th con estilo uppercase letter-spaced
// ─────────────────────────────────────────────────────────────────────────────
interface HeaderCellProps {
  align?: "left" | "right" | "center";
  width?: string | number;
  sticky?: boolean;
  children: ReactNode;
}

export function HeaderCell(props: HeaderCellProps): JSX.Element {
  const { align = "left", width, sticky = true, children } = props;
  return (
    <Box
      component="th"
      sx={{
        textAlign: align,
        fontWeight: 500,
        fontSize: "11.5px",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: "var(--ink-3)",
        padding: "10px 12px",
        borderBottom: "1px solid var(--border)",
        backgroundColor: "var(--bg)",
        ...(sticky && {
          position: "sticky",
          top: 0,
          zIndex: 1,
        }),
        ...(width !== undefined && {
          width: typeof width === "number" ? `${width}px` : width,
        }),
      }}
    >
      {children}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BodyRow — tr con hover
// ─────────────────────────────────────────────────────────────────────────────
interface BodyRowProps {
  children: ReactNode;
  onClick?: () => void;
}

export function BodyRow(props: BodyRowProps): JSX.Element {
  const { children, onClick } = props;
  return (
    <Box
      component="tr"
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        "&:hover": { backgroundColor: "var(--bg)" },
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      {children}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BodyCell — td con padding y borde inferior
// ─────────────────────────────────────────────────────────────────────────────
interface BodyCellProps {
  align?: "left" | "right" | "center";
  muted?: boolean;
  width?: string | number;
  children: ReactNode;
}

export function BodyCell(props: BodyCellProps): JSX.Element {
  const { align = "left", muted = false, width, children } = props;
  return (
    <Box
      component="td"
      sx={{
        textAlign: align,
        padding: "var(--pad-row) 12px",
        borderBottom: "1px solid var(--border)",
        color: muted ? "var(--ink-3)" : "var(--ink-1)",
        fontVariantNumeric: "tabular-nums",
        verticalAlign: "middle",
        ...(width !== undefined && {
          width: typeof width === "number" ? `${width}px` : width,
        }),
      }}
    >
      {children}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RankNum — número plano alineado derecha
// ─────────────────────────────────────────────────────────────────────────────
export function RankNum({ n }: { n: number }): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        fontSize: "11px",
        color: "var(--ink-4)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {n}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SkuBadge — chip mono font con bg gris claro
// ─────────────────────────────────────────────────────────────────────────────
export function SkuBadge({ sku }: { sku: string }): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: "var(--font-mono)",
        fontSize: "11px",
        color: "var(--ink-3)",
        backgroundColor: "var(--bg-2)",
        padding: "2px 5px",
        borderRadius: "4px",
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {sku}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ProductCell — SKU badge + nombre del producto (truncate)
// ─────────────────────────────────────────────────────────────────────────────
interface ProductCellProps {
  sku?: string | null;
  name: string;
  subtitle?: string | null;
}

export function ProductCell(props: ProductCellProps): JSX.Element {
  const { sku, name, subtitle } = props;
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}
    >
      {sku && <SkuBadge sku={sku} />}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          component="span"
          sx={{
            fontWeight: 500,
            color: "var(--ink-1)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
        >
          {name}
        </Box>
        {subtitle && (
          <Box
            component="span"
            sx={{
              fontSize: "11.5px",
              color: "var(--ink-3)",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DeltaPill — chip +X% / -X% con bg suave
// ─────────────────────────────────────────────────────────────────────────────
interface DeltaPillProps {
  delta: number | null | undefined;
  // Si los valores positivos cuentan como "negativos" (ej. erosión donde menos es mejor)
  invert?: boolean;
  // Texto a mostrar cuando delta es null/undefined (default "Sin datos")
  emptyLabel?: string;
}

export function DeltaPill(props: DeltaPillProps): JSX.Element {
  const { delta, invert = false, emptyLabel = "Sin datos" } = props;

  if (delta === null || delta === undefined || !Number.isFinite(delta)) {
    return (
      <Box
        component="span"
        sx={{
          fontSize: "11.5px",
          color: "var(--ink-3)",
        }}
      >
        {emptyLabel}
      </Box>
    );
  }

  const isPos = invert ? delta < 0 : delta >= 0;
  const palette = isPos
    ? { color: "var(--pos)", bg: "var(--pos-soft)" }
    : { color: "var(--neg)", bg: "var(--neg-soft)" };
  const sign = delta > 0 ? "+" : "";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "3px",
        padding: "2px 6px",
        borderRadius: "4px",
        fontSize: "11.5px",
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: palette.color,
        backgroundColor: palette.bg,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {sign}
      {delta.toFixed(1)}%
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tag — etiqueta con tono semántico (Alto/Medio/Bajo/Crítico/Atención)
// ─────────────────────────────────────────────────────────────────────────────
export type TagTone = "good" | "warn" | "alert" | "neutral";

interface TagProps {
  label: string;
  tone?: TagTone;
}

export function Tag(props: TagProps): JSX.Element {
  const { label, tone = "neutral" } = props;
  const palette =
    tone === "good"
      ? { color: "var(--pos)", bg: "var(--pos-soft)" }
      : tone === "warn"
        ? { color: "var(--warn)", bg: "var(--warn-soft)" }
        : tone === "alert"
          ? { color: "var(--neg)", bg: "var(--neg-soft)" }
          : { color: "var(--ink-2)", bg: "var(--bg-2)" };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        padding: "2px 7px",
        fontSize: "11px",
        borderRadius: "4px",
        backgroundColor: palette.bg,
        color: palette.color,
        fontWeight: 500,
      }}
    >
      {label}
    </Box>
  );
}
